import React, { useEffect, useState, useCallback } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useNavigate } from "react-router-dom";
import { getErrorMessageFromApiError } from "@fmc-digital/assets";
import {
  useTriggerMFAPost,
  useValidateMFAPost,
} from "@fmc-digital/auth-api-generated";
import { getCurrentLanguage } from "@fmc-digital/i18n";
import { ErrorBanner, cn } from "@fmc-digital/ui";
import { ROUTES } from "@/constants/routes.constants";
import { RecoverPasswordForm } from "../RecoverPasswordForm";
import {
  MFACodeContainer,
  type FactorState,
} from "../MFACodeContainer/MFACodeContainer";
import { MFASelectorContainer } from "../MFASelectorContainer/MFASelectorContainer";
import { ResetPasswordForm } from "../ResetPasswordForm";
import { config } from "@/config";
import { loadConfig } from "@/config/auth.config";
import {
  RecoverUsernameProvider,
  useRecoverUsername,
} from "@/contexts/RecoverUsernameContext";
import { extractErrorCode } from "@/utils/helpers";
import type { ScreenName } from "./forgot-password-container.types";
import { forgotPasswordContainerStyles } from "./forgot-password-container.styles";
import { trackForgotPasswordEvent, ForgotPasswordEvent } from "@/utils/analytics.utils";

// Main component that uses the context
const ForgotPasswordContainerContent: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { response, factorsList, guid, clearResponse } = useRecoverUsername();
  const [recaptchaKey, setRecaptchaKey] = useState<string>("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [currentScreen, setCurrentScreen] =
    useState<ScreenName>("personalInfo");
  const [selectedFactor, setSelectedFactor] = useState<FactorState | null>(
    null,
  );
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaErrorCode, setMfaErrorCode] = useState<string | null>(null);
  const [initialFactorType, setInitialFactorType] = useState<
    "email" | "sms" | undefined
  >(undefined);

  // MFA API hooks
  const { mutate: triggerMFA, isPending: isTriggerMFALoading } =
    useTriggerMFAPost();

  const { mutate: validateMFA, isPending: isValidateMFALoading } =
    useValidateMFAPost();

  useEffect(() => {
    // Load config and get reCAPTCHA site key
    const initializeConfig = async () => {
      try {
        const cfg = await loadConfig();
        if (cfg.googleRecaptchaSiteKey) {
          setRecaptchaKey(cfg.googleRecaptchaSiteKey);
          setConfigLoaded(true);
        } else {
          console.error("reCAPTCHA site key not found in config");
          setConfigLoaded(true); // Still render to show error
        }
      } catch (error) {
        console.error("Failed to load config:", error);
        setConfigLoaded(true);
      }
    };
    initializeConfig();
  }, []);

  // Update screen when response is received
  useEffect(() => {
    if (
      response &&
      factorsList.length > 0 &&
      currentScreen === "personalInfo"
    ) {
      setInitialFactorType(undefined); // Reset to allow default selection
      setCurrentScreen("mfaSelection");
    }
  }, [response, factorsList, currentScreen]);

  // Handle fallback when no factor is selected but we're on mfaCode screen
  useEffect(() => {
    if (currentScreen === "mfaCode" && !selectedFactor) {
      console.log(
        "[ForgotPasswordContainer] No selectedFactor on mfaCode screen, returning to mfaSelection",
      );
      setCurrentScreen("mfaSelection");
    }
  }, [currentScreen, selectedFactor]);

  // Handle fallback when no factors available but we're on mfaSelection screen
  useEffect(() => {
    if (currentScreen === "mfaSelection") {
      if (!response || factorsList.length === 0) {
        setCurrentScreen("personalInfo");
      } else {
        // Check if there are any email or sms factors
        const hasValidFactors = factorsList.some((factor) => {
          const factorType = factor.factorType?.toLowerCase() ?? "";
          return factorType.includes("email") || factorType.includes("sms");
        });
        if (!hasValidFactors) {
          setCurrentScreen("personalInfo");
        }
      }
    }
  }, [currentScreen, response, factorsList]);

  // Handle MFA factor selection
  const handleMFANext = useCallback(
    (factor: {
      id: string;
      factorType: "email" | "sms";
      factorLabel: string;
      profile: { email?: string; phoneNumber?: string };
    }) => {
      // Track factor selection (non-blocking)
      try {
        if (factor.factorType === "email") {
          trackForgotPasswordEvent(ForgotPasswordEvent.OTP_EMAIL_FACTOR_SELECTED);
        } else if (factor.factorType === "sms") {
          trackForgotPasswordEvent(ForgotPasswordEvent.OTP_PHONE_FACTOR_SELECTED);
        }
      } catch (trackingError) {
        // Silently fail tracking - don't disrupt user flow
      }

      // Store selected factor and trigger MFA code sending
      if (guid) {
        setMfaError(null);
        setMfaErrorCode(null);
        // Ensure we're on mfaSelection screen before making the API call
        // This prevents any race conditions
        setCurrentScreen("mfaSelection");
        // Don't set selectedFactor yet - only set it on success
        // This prevents the screen from transitioning if the API fails

        // Trigger MFA code sending API call
        triggerMFA(
          { data: { mfaId: factor.id } },
          {
            onSuccess: () => {
              console.log(
                "[ForgotPasswordContainer] triggerMFA succeeded, transitioning to mfaCode",
              );
              
              // Track OTP triggered event based on factor type (non-blocking)
              try {
                if (factor.factorType === "email") {
                  trackForgotPasswordEvent(ForgotPasswordEvent.OTP_EMAIL_TRIGGERED);
                } else if (factor.factorType === "sms") {
                  trackForgotPasswordEvent(ForgotPasswordEvent.OTP_PHONE_TRIGGERED);
                }
              } catch (trackingError) {
                // Silently fail tracking - don't disrupt user flow
              }
              
              // Only set selectedFactor and transition screen on success
              setSelectedFactor({
                selectedFactorId: factor.id,
                factorType: factor.factorType,
                factorLabel: factor.factorLabel,
                profile: factor.profile,
              });
              // Show MFA code screen after successfully triggering MFA
              setCurrentScreen("mfaCode");
            },
            onError: (error: unknown) => {
              console.error(
                "[ForgotPasswordContainer] Failed to trigger MFA:",
                error,
              );
              setMfaErrorCode(extractErrorCode(error));
              try {
                const lang = getCurrentLanguage() ?? "en";
                const errorMessage = getErrorMessageFromApiError(error, lang);
                setMfaError(errorMessage);
              } catch {
                // Fallback if i18n is not initialized
                const errorMessage = getErrorMessageFromApiError(error, "en");
                setMfaError(errorMessage);
              }
              // Explicitly ensure we stay on mfaSelection screen on error
              // Clear selectedFactor to prevent any screen transition
              setSelectedFactor(null);
              setCurrentScreen("mfaSelection");
              console.log(
                "[ForgotPasswordContainer] Error occurred, staying on mfaSelection screen",
              );
            },
          },
        );
      }
    },
    [guid, triggerMFA],
  );

  const handleMFACancel = useCallback(() => {
    clearResponse();
    setCurrentScreen("personalInfo");
    setSelectedFactor(null);
    setInitialFactorType(undefined);
    // Clear stored GUID from config when flow is cancelled
    config.setEnv("recoverPasswordGuid", "");
    navigate(ROUTES.LOGIN);
  }, [clearResponse, navigate]);

  // Handle MFA code verification
  const handleMFAVerify = useCallback(
    (code: string) => {
      setMfaError(null);

      if (!guid) {
        setMfaError("Session expired. Please start over.");
        return;
      }

      // Validate MFA code API call
      validateMFA(
        { data: { mfaCode: code } },
        {
          onSuccess: (response) => {
            // Handle successful verification
            console.log("MFA verification successful:", response);
            
            // Track success event based on factor type (non-blocking)
            try {
              if (selectedFactor?.factorType === "email") {
                trackForgotPasswordEvent(ForgotPasswordEvent.OTP_EMAIL_VALIDATION_SUCCESS);
              } else if (selectedFactor?.factorType === "sms") {
                trackForgotPasswordEvent(ForgotPasswordEvent.OTP_PHONE_VALIDATION_SUCCESS);
              }
            } catch (trackingError) {
              // Silently fail tracking - don't disrupt user flow
            }
            
            // Keep guid in context for reset password screen
            // Don't clear guid - it's needed for reset password API call
            // Navigate to reset password screen
            setCurrentScreen("resetPassword");
          },
          onError: (error: unknown) => {
            console.error("MFA verification failed:", error);

            // Extract error code from error object
            const errorCode = extractErrorCode(error);
            setMfaErrorCode(errorCode);
            
            // Track failure events (non-blocking)
            try {
              // Check if account is locked
              if (errorCode === "E0000069" || errorCode === "E0000068" || errorCode === "E0000047") {
                trackForgotPasswordEvent(ForgotPasswordEvent.ACCOUNT_LOCKED);
              } else if (selectedFactor?.factorType === "email") {
                // Track failure event based on factor type
                trackForgotPasswordEvent(ForgotPasswordEvent.OTP_EMAIL_VALIDATION_FAILED);
              } else if (selectedFactor?.factorType === "sms") {
                trackForgotPasswordEvent(ForgotPasswordEvent.OTP_PHONE_VALIDATION_FAILED);
              }
            } catch (trackingError) {
              // Silently fail tracking - don't disrupt user flow
            }

            try {
              const lang = getCurrentLanguage() ?? "en";
              const errorMessage = getErrorMessageFromApiError(error, lang);
              setMfaError(errorMessage);
            } catch {
              // Fallback if i18n is not initialized
              const errorMessage = getErrorMessageFromApiError(error, "en");
              setMfaError(errorMessage);
            }
          },
        },
      );
    },
    [guid, validateMFA, navigate],
  );

  // Handle resend MFA code
  const handleMFAResend = useCallback(() => {
    setMfaError(null);
    setMfaErrorCode(null);

    if (!guid || !selectedFactor?.selectedFactorId) {
      setMfaError("Unable to resend code. Please start over.");
      return;
    }

    // Trigger MFA code resend API call
    triggerMFA(
      { data: { mfaId: selectedFactor.selectedFactorId } },
      {
        onSuccess: () => {
          // Code resent successfully
          console.log("MFA code resent successfully");
          
          // Track resend event based on factor type (non-blocking)
          try {
            if (selectedFactor.factorType === "email") {
              trackForgotPasswordEvent(ForgotPasswordEvent.OTP_EMAIL_RESENT);
            } else if (selectedFactor.factorType === "sms") {
              trackForgotPasswordEvent(ForgotPasswordEvent.OTP_PHONE_RESENT);
            }
          } catch (trackingError) {
            // Silently fail tracking - don't disrupt user flow
          }
        },
        onError: (error) => {
          console.error("Failed to resend MFA code:", error);

          // Extract error code from error object
          const errorCode = extractErrorCode(error);
          setMfaErrorCode(errorCode);

          try {
            const lang = getCurrentLanguage() ?? "en";
            const errorMessage = getErrorMessageFromApiError(error, lang);
            setMfaError(errorMessage);
          } catch {
            // Fallback if i18n is not initialized
            const errorMessage = getErrorMessageFromApiError(error, "en");
            setMfaError(errorMessage);
          }
        },
      },
    );
  }, [guid, selectedFactor?.selectedFactorId, triggerMFA]);

  // Handle navigate back to factor selection
  const handleNavigateToFactorSelection = useCallback(
    (targetFactorType: "email" | "sms") => {
      setInitialFactorType(targetFactorType);
      setCurrentScreen("mfaSelection");
      setMfaError(null);
      // Clear selectedFactor so user can select again
      setSelectedFactor(null);
    },
    [],
  );

  const handleErrorClear = useCallback(() => {
    setMfaError(null);
    setMfaErrorCode(null);
  }, []);

  // Early returns AFTER all hooks
  if (!configLoaded) {
    return (
      <div data-testid="forgot-password-loading">
        Loading...
      </div>
    );
  }

  if (!recaptchaKey) {
    return (
      <div data-testid="forgot-password-recaptcha-error">
        <ErrorBanner message="Error: reCAPTCHA configuration is missing" />
      </div>
    );
  }

  // Render based on current screen
  if (currentScreen === "mfaCode") {
    // Only render mfaCode screen if we have a selectedFactor (API succeeded)
    // If no selectedFactor, the useEffect will handle transitioning back to mfaSelection
    if (!selectedFactor) {
      console.log(
        "[ForgotPasswordContainer] currentScreen is mfaCode but no selectedFactor, returning null (useEffect will handle)",
      );
      return null; // Will be handled by useEffect
    }
    return (
      <div
        className={cn(forgotPasswordContainerStyles({ screen: "mfaCode" }))}
        data-testid="forgot-password-mfa-code-screen"
      >
        <MFACodeContainer
          factorState={selectedFactor}
          factorsList={factorsList}
          onVerify={(code) => handleMFAVerify(code)}
          onCancel={handleMFACancel}
          onNavigateToFactorSelection={handleNavigateToFactorSelection}
          onResend={handleMFAResend}
          error={mfaError}
          errorCode={mfaErrorCode}
          onErrorClear={handleErrorClear}
          isLoading={isValidateMFALoading}
        />
      </div>
    );
  }

  if (currentScreen === "mfaSelection") {
    if (response && factorsList.length > 0) {
      return (
        <div
          className={cn(forgotPasswordContainerStyles({ screen: "mfaSelection" }))}
          data-testid="forgot-password-mfa-selection-screen"
        >
          <MFASelectorContainer
            onNext={handleMFANext}
            onCancel={handleMFACancel}
            factors={factorsList}
            isLoading={isTriggerMFALoading}
            initialFactorType={initialFactorType}
            error={mfaError}
            onErrorClear={handleErrorClear}
          />
        </div>
      );
    }
    return null; // Will be handled by useEffect
  }

  if (currentScreen === "resetPassword") {
    return (
      <div
        className={cn(forgotPasswordContainerStyles({ screen: "resetPassword" }))}
        data-testid="forgot-password-reset-password-screen"
      >
        <ResetPasswordForm />
      </div>
    );
  }

  // Default: personalInfo screen
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
      <div
        className={cn(forgotPasswordContainerStyles({ screen: "personalInfo" }))}
        data-testid="forgot-password-personal-info-screen"
      >
        <RecoverPasswordForm />
      </div>
    </GoogleReCaptchaProvider>
  );
};

// Main component that provides the context
export const ForgotPasswordContainer: React.FunctionComponent = () => {
  return (
    <RecoverUsernameProvider>
      <ForgotPasswordContainerContent />
    </RecoverUsernameProvider>
  );
};
